import { Injectable, OnDestroy } from "@angular/core";
import { BaseService } from '../../common/services/base.service';
import { CacheService } from '../../common/services/cache.service';
import { NotificationsRequest, Request } from './notifications-menu.model';
import { Observable, tap, of, Subject, Subscription, interval, takeUntil } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { CACHE_KEYS } from '../../common/constants/cache-keys';
import { NotificationResults, NotificationsResponse } from "./notifications-menu.viewmodel";
import { PageService } from "../../common/services/page/page.service";
import { ContentModel } from "../../common/models/content.model";
import { PageJsonResults } from "../../common/services/page/page-data.viewmodel";

@Injectable({
  providedIn: "root",
})
export class NotificationsMenuService implements OnDestroy {
  private getNotificationsSubject = new Subject<NotificationsResponse>();
  private notificationSubscription: Subscription;
  userName?: string
  constructor(private baseService: BaseService, private cacheService: CacheService, private pageService: PageService) { }

  ngOnDestroy(): void {
    this.stopNotifications();
  }

  private mockedNotifications: NotificationResults[] = [
    {
      "id": "320c151a",
      "description": "<p>MEDIA ADVISORY: Stellantis to Announce Third Quarter 2024 Shipments and Revenues on October 31</p>\r\n",
      "endDate": "2024-11-07T18:30:00.000+00:00",
      "startDate": "2024-11-04T18:30:00.000+00:00",
      "title": "Third Quarter 2024 Shipments Announce ",
      "image": "/content/dam/lpmi/global/top-banner/STELLANTIS-LOGO-transparent.png",
      "brandTags": [],
      "geoTags": null,
      "roleTags": [],
      "linkPath": "/content/lpmi/ee/it/en/news-articles/news1",
      "isRead": false,
      "tooltip": null,
      "isDeepLinkAvailable": null
    }
  ]

  private setNotifications(notifications: NotificationsResponse) {
    this.getNotificationsSubject.next(notifications);
  }

  getNotifications(): Observable<NotificationsResponse> {
    return this.getNotificationsSubject.asObservable();
  }

  triggerNotificationsAdhoc() {
    this.startNotificationsPolling();
  }

  private getMockedNotifications(): Observable<NotificationsResponse> {
    return of({ results: this.mockedNotifications });
  }

  startNotificationsPolling(): void {
    this.stopNotifications();
    let apiUrl = `/v1/lpmi/portal/getNotificationsByUserName`;
    let request = new Request();
    request.userDetails = this.pageService.getUserDetails();
    // Add region from login-selection cache
    const loginSelections = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_SELECTIONS) as any;
    if (loginSelections?.region) {
      const { market, ...remainingUserDetails } = (request.userDetails || {}) as any;
      request.userDetails = {
        ...remainingUserDetails,
        market,
        region: loginSelections.region,
      } as any;
    }
    let isFirstLoad = true;
    this.notificationSubscription = interval(1 * 30 * 1000)
      .pipe(
        startWith(0),
        switchMap(() => {
          if (isFirstLoad) {
            isFirstLoad = false;
            return this.baseService.post(`${apiUrl}`, request, 'API', '', false, false);
          } else {
            return this.baseService.post(`${apiUrl}`, request, 'API', '', false, true);
          }
        })
      )
      .subscribe((response: NotificationsResponse) => {
        this.setNotifications(response);
      });
  }

  stopNotifications() {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
  }

  markAllasRead(unreadNotifications: string[]): Observable<any> {
    let apiUrl = '/v1/lpmi/portal/updateNotificationByUserName';
    let request = this.createNotificationRequest(unreadNotifications);
    return this.baseService
      .post(
        `${apiUrl}`,
        request,
        'API'
      )
      .pipe(
        tap((response: any) => {
          this.triggerNotificationsAdhoc();
        })
      );
  }

  private createNotificationRequest(unreadNotifications: string[]) {
    const loginData = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_DATA);
    let request = new NotificationsRequest();
    request.userDetails = this.pageService.getUserDetails();
    request.userName = loginData?.userName;
    request.notificationIds = unreadNotifications;
    // Add region from login-selection cache
    const loginSelections = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_SELECTIONS) as any;
    if (loginSelections?.region) {
      const { market, ...remainingUserDetails } = (request.userDetails || {}) as any;
      request.userDetails = {
        ...remainingUserDetails,
        market,
        region: loginSelections.region,
      } as any;
    }
    return request;
  }

  getContent(componentType: string, routeData: PageJsonResults): ContentModel[] {
    return this.pageService.getPageContent(componentType, routeData);
  }

}
